import dayjs from "dayjs";
import { useEffect } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { GraphQLClient } from "graphql-request";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { useStateContext } from "contexts/auth-context";
import { Navigate } from "react-router-dom";

import { useDisable } from "hooks/use-disable";

import {
  CompanyFindUniqueQuery,
  useCompanyFindUniqueQuery,
  useCompanyUpdateMutation,
} from "generated/general";
import { createGraphQLClientWithMiddleware } from "services/graphqlClient";

import {
  companySchema,
  companyValidation,
} from "components/Form/Company/schema";
import LoadingUI from "components/UI/LoadingUI";
import HeaderLayout from "components/UI/HeaderLayout";
import ButtonLayout from "components/UI/ButtonLayout";
import CompanyDetail from "components/Form/Company/Detail";
import CompanyAddress from "components/Form/Company/Address";
import BottomNavbar from "components/UI/Navbar/BottomNavbar";
import { CustomizedBox } from "components/Custom/CustomizedBox";
import CustomizedButton from "components/Custom/CustomizedButton";
import CompanyContactChannel from "components/Form/Company/ContactChannel";
import CustomizedBreadcrumbs from "components/Custom/CustomizedBreadcrumbs";
import CustomizedMenuOptions from "components/Custom/CustomizedMenuOptions";

import { ICompany } from "types/Setting/Company";
import { IBreadcrumbsAndMenu } from "types/global";

import { uploadFileToS3 } from "utils/s3";
import { errorMessageFormatter } from "utils/Global";
import { Typography } from "@mui/material";

const selectModifyOptions = [
  {
    value: "แก้ไข",
    disabled: false,
  },
];

const CompanySetting = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { permissions },
  } = useStateContext();

  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("setting.index"),
      to: "/setting",
    },
    {
      name: t("setting.company.index"),
      to: "/setting/company",
    },
  ];

  const methods = useForm<ICompany>({
    defaultValues: companySchema,
    resolver: yupResolver<any>(companyValidation),
    mode: "onChange",
  });

  const { handleSubmit, reset } = methods;

  const [disabled, setDisabled] = useDisable();

  const graphqlClient: GraphQLClient =
    createGraphQLClientWithMiddleware("general");

  const { data, isLoading, isSuccess, refetch } =
    useCompanyFindUniqueQuery<CompanyFindUniqueQuery>(graphqlClient, {
      uniqueInput: {
        id: 1,
      },
    });

  useEffect(() => {
    if (isSuccess) {
      const { CompanyFindUnique } = data;
      const companyData = CompanyFindUnique as ICompany;

      const formatData: ICompany = {
        ...companyData,
        register_date: dayjs(companyData.register_date),
        vat_registration_date: dayjs(companyData.vat_registration_date),
      };
      reset(formatData);
    }
  }, [data, isSuccess, reset]);

  useEffect(() => {
    setDisabled(true);
    return () => setDisabled(false);
  }, [setDisabled]);

  const { mutateAsync: update, isLoading: isUpdating } =
    useCompanyUpdateMutation<Error>(graphqlClient);

  const updateCompanyHandler = async (data: ICompany) => {
    try {
      const { type, id, ...otherData } = data;
      let img_url: string[] = [];
      if (data.img_url && data.img_url.length > 0) {
        const filteredFile = data.img_url.filter(
          (img: string | File) => img instanceof File
        );

        if (filteredFile && filteredFile.length > 0) {
          const { Location } = await uploadFileToS3(
            filteredFile[0],
            "company",
            id?.toString() || ""
          );
          img_url.push(Location);
        } else {
          img_url = data.img_url;
        }
      }
      const { CompanyUpdate } = await update({
        uniqueInput: {
          id: 1,
        },
        data: {
          ...otherData,
          img_url,
          type: type,
        },
      });
      enqueueSnackbar("แก้ไขข้อมูลกิจการสำเร็จ", {
        variant: "success",
      });
      const updatedData = CompanyUpdate as ICompany;
      const formatData: ICompany = {
        ...updatedData,
        register_date: dayjs(updatedData.register_date),
        vat_registration_date: dayjs(updatedData.vat_registration_date),
      };
      reset(formatData);
      refetch();
      setDisabled(true);
    } catch (err) {
      const formatError = errorMessageFormatter(err);
      enqueueSnackbar(formatError || "แก้ไขข้อมูลกิจการไม่สำเร็จ", {
        variant: "error",
      });
    }
  };

  const cancelEditHandler = () => {
    reset();
    setDisabled(true);
  };

  if (permissions && !permissions.company?.view) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (isLoading || isUpdating) {
    return <LoadingUI />;
  }

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <HeaderLayout marginBottom={0}>
        <Typography variant="h5">{t("setting.company.index")}</Typography>
        {permissions?.company?.update && (
          <CustomizedMenuOptions
            label={"ตัวเลือก"}
            options={selectModifyOptions}
            onSelect={(e) => {
              const value = e.target as HTMLElement;
              switch (value.innerText) {
                case "แก้ไข":
                  setDisabled(false);
                  break;
                default:
                  break;
              }
            }}
          />
        )}
      </HeaderLayout>
      <FormProvider {...methods}>
        <CompanyDetail />
        <CompanyContactChannel />
        <CustomizedBox padding={"0 1.5rem"}>
          <CompanyAddress
            addressList={data?.CompanyFindUnique?.address_list || []}
          />
        </CustomizedBox>
        {!disabled && permissions?.company?.update && (
          <BottomNavbar>
            <ButtonLayout>
              <CustomizedButton
                size="medium"
                title={t("button.cancel")}
                variant="outlined"
                onClick={cancelEditHandler}
              />
              <CustomizedButton
                title={t("button.save")}
                variant="contained"
                onClick={handleSubmit(updateCompanyHandler)}
              />
            </ButtonLayout>
          </BottomNavbar>
        )}
      </FormProvider>
    </>
  );
};

export default CompanySetting;
